/**
 * @voltxampere/engine — Formula template: op-amp non-inverting (S56b)
 *
 * Subgraph: V_in → V_+; feedback divider R_f (V_out → V_-) and
 * R_g (V_- → GND).
 *
 *     V_in ── V_+ (op-amp non-inv in)
 *     V_out ── R_f ── V_- ── R_g ── GND
 *
 * Closed-loop gain V_out / V_in = 1 + R_f / R_g.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { asNumber, buildNetIndex, findVoltageSourceDriving, formatLatexValue, isGroundNet, netOf, opampTerminal, opampsInCircuit, } from "../helpers.js";
const SYMBOLIC = "\\frac{V_{out}}{V_{in}} = 1 + \\frac{R_f}{R_g}";
export const opampNonInvertingTemplate = {
    id: "opamp-non-inverting",
    specificity: "high",
    displayNameKey: {
        tr: "formula.opamp-non-inverting.name",
        en: "formula.opamp-non-inverting.name",
    },
    match(circuit, selected) {
        const opamps = opampsInCircuit(circuit, selected);
        if (opamps.length === 0)
            return null;
        const netIndex = buildNetIndex(circuit);
        for (const op of opamps) {
            const inv = opampTerminal(op, "inverting");
            const noninv = opampTerminal(op, "non-inverting");
            const out = opampTerminal(op, "output");
            if (inv === null || noninv === null || out === null)
                continue;
            // V_+ must be driven by a voltage source.
            const vSource = findVoltageSourceDriving(circuit, noninv);
            if (vSource === null)
                continue;
            // Find R_f (V_- ↔ V_out) and R_g (V_- ↔ GND).
            const invTerminals = netIndex.get(inv) ?? [];
            const invResistors = invTerminals.filter((t) => t.component.type === "resistor");
            if (invResistors.length < 2)
                continue;
            let rf = null;
            let rg = null;
            for (const candidate of invResistors) {
                const otherNet = netOf(candidate.component, "p1") === inv
                    ? netOf(candidate.component, "p2")
                    : netOf(candidate.component, "p1");
                if (otherNet === null)
                    continue;
                if (otherNet === out && rf === null)
                    rf = candidate.component;
                else if (isGroundNet(circuit, otherNet) && rg === null)
                    rg = candidate.component;
            }
            if (rf === null || rg === null)
                continue;
            const rfVal = asNumber(rf.params.resistance);
            const rgVal = asNumber(rg.params.resistance);
            if (rfVal === null || rgVal === null || rgVal === 0)
                continue;
            const gain = 1 + rfVal / rgVal;
            const rfLatex = formatLatexValue(rfVal, "\\Omega");
            const rgLatex = formatLatexValue(rgVal, "\\Omega");
            const gainLatex = gain.toPrecision(3);
            return {
                templateId: "opamp-non-inverting",
                involvedComponents: [op.id, rf.id, rg.id, vSource.id],
                involvedNets: [inv, noninv, out],
                symbolic: SYMBOLIC,
                numeric: {
                    formula: `\\frac{V_{out}}{V_{in}} = 1 + \\frac{${rfLatex}}{${rgLatex}} = ${gainLatex}`,
                    result: gain,
                    unit: "V/V",
                },
                specificity: "high",
            };
        }
        return null;
    },
};
//# sourceMappingURL=opamp-non-inverting.js.map